"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Questions() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const leftCol = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const rightCol = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What is the difference between a cardiologist and a primary care physician?",
    answer: "A cardiologist is a specialist who focuses exclusively on the cardiovascular system, including the heart and blood vessels. While your primary care physician manages your overall health, our team provides advanced diagnostic testing, interventional procedures, and specialized management for complex heart conditions."
  }, {
    question: "Do I need a referral to schedule an appointment?",
    answer: "No. You may contact our office directly to schedule an appointment without a referral."
  }, {
    question: "What should I bring to my first cardiology evaluation?",
    answer: "Please bring a valid photo ID, your current insurance card, a list of all medications you are currently taking, and any recent medical records or test results from other providers. This helps Dr. Carrillo-Jimenez provide the most accurate assessment during your visit."
  }, {
    question: "How long do cardiac PET and SPECT stress tests take?",
    answer: "A cardiac PET stress test takes approximately 45 minutes, while a cardiac SPECT stress test takes approximately three hours."
  }, {
    question: "What is an echocardiogram?",
    answer: "An echocardiogram is a non-invasive ultrasound of the heart. It uses sound waves to create detailed images of your heart's chambers, valves, and walls. This allows us to evaluate how well your heart is pumping and identify any structural abnormalities."
  }, {
    question: "Do you treat peripheral arterial disease (PAD)?",
    answer: "Yes, we specialize in the diagnosis and treatment of peripheral arterial disease. We offer comprehensive vascular care, including minimally invasive interventions to improve blood flow and alleviate symptoms like leg pain or cramping."
  }, {
    question: "What are the signs of heart failure?",
    answer: "Common symptoms include persistent shortness of breath, fatigue, swelling in the legs or ankles, and rapid or irregular heartbeat. If you are experiencing these symptoms, it is important to schedule a professional evaluation as soon as possible."
  }, {
    question: "Do you offer same-day appointments?",
    answer: "Yes. Same-day appointments may be available depending on your insurance requirements and appointment availability. Please call our office, and our staff will help you find the earliest available appointment."
  }, {
    question: "Is Dr. Carrillo-Jimenez board-certified?",
    answer: "Yes. Dr. Rodolfo Carrillo-Jimenez is board certified in Cardiovascular Disease and Interventional Cardiology and is a Fellow of the American College of Cardiology (FACC). He brings extensive experience in interventional cardiology and vascular medicine to our Delray Beach and Boynton Beach locations."
  }, {
    question: "What insurance plans do you accept?",
    answer: "Please contact our office directly with your insurance information. Our staff will help you verify coverage before your appointment."
  }];
  return <section data-section-id="948" id="common-questions" className="bg-[#F8FAFC] pt-12 pb-24 font-default text-[#152238]">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={header.ref} className={`text-center mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.24em] uppercase text-[#A32D2D] mb-5 font-semibold">
            · Patient Information ·
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#152238] mb-5 leading-tight font-semibold">
            Frequently Asked Questions
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Clear answers to help you navigate your cardiovascular care journey with confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div ref={leftCol.ref} className={`lg:sticky lg:top-24 transition-all duration-700 ease-out ${leftCol.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="border-l-2 border-[#A32D2D] pl-8 py-2 mb-10">
                <p className="font-serif italic text-[#152238] leading-relaxed" style={{
                fontSize: "22px"
              }}>
                  "Your heart health is our priority. We are here to answer every question with precision and care."
                </p>
              </div>

              <div className="mt-10 pt-8 border-t border-slate-300">
                <p className="text-sm text-slate-600 mb-3 tracking-wide">
                  Still have questions?
                </p>
                <Link to="/contact-us/" className="inline-flex items-center gap-2 font-serif text-2xl text-[#152238] hover:text-[#A32D2D] transition-colors duration-300 group">
                  <span className="italic">— contact our team</span>
                </Link>
              </div>
            </div>
          </div>

          <div ref={rightCol.ref} className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className={`mb-3 border border-slate-200 bg-white shadow-sm transition-all duration-700 ease-out data-[state=open]:border-[#55C0ED] data-[state=open]:bg-[#F2FAFD] rounded-xl overflow-hidden ${rightCol.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  <AccordionTrigger className="font-default text-[#152238] hover:no-underline py-5 px-5 group [&>svg]:hidden">
                    <span className="text-left leading-snug pr-4 font-semibold" style={{
                  fontSize: "1.125rem"
                }}>
                      {faq.question}
                    </span>
                    <Plus className="size-5 shrink-0 text-[#A32D2D] transition-transform duration-500 ease-out group-data-[state=open]:rotate-[135deg]" strokeWidth={1.75} />
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-slate-600 text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}
